import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Menu, { MenuProps } from '@material-ui/core/Menu';
import MenuItem, { MenuItemProps } from '@material-ui/core/MenuItem';
import ListItem from '@material-ui/core/ListItem';
import Typography, { TypographyProps } from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import MoreVertIcon from '@material-ui/icons/MoreVert';

interface MenuOptionProps extends MenuItemProps {
  label: string;
}

type MenuPropsOmitOpen = Omit<MenuProps, 'open'>;

export interface MenuSectionProps extends MenuPropsOmitOpen {
  label: string;
  options: MenuOptionProps[];
  title?: string;
}

export interface MultiMenuProps extends AppHeaderOptionsMenuProps {
  handleClose: () => void;
  handleKey: React.KeyboardEventHandler<HTMLDivElement>;
  anchorEl?: HTMLButtonElement | null;
}

export interface AppHeaderOptionsMenuProps {
  optionSections: MenuSectionProps[];
  keepMounted?: boolean;
  id?: string;
  menuClassName?: string;
  closeOnClick?: boolean;
  menuVariant?: 'menu' | 'selectedMenu';
  buttonClassName?: string;
  buttonId?: string;
  icon?: React.ReactNode;
  iconClassName?: string;
  selected?: { section: number; option: number } | null;
  textColor?: string;
  separatorColor?: string;
  buttonSize?: 'small' | 'medium';
  'aria-label'?: string;
  defaultOptionClickHandler?: React.ReactEventHandler<HTMLLIElement>;
}

interface SectionTitleProps {
  children?: React.ReactNode;
  className?: string;
  textClassName?: string;
  typographyVariant?: TypographyProps['variant'];
  renderEmptyTitleField?: boolean;
}

const useStyles = (textColor: string, separatorColor: string): Function =>
  makeStyles((theme: Theme) => {
    const fontSize = '1rem';

    return {
      menuContainer: {
        height: '100%',
        width: '100%'
      },
      multiMenuContainer: {
        display: 'flex',
        paddingLeft: theme.spacing(1),
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(2),
        '&:hover': {
          outline: 'none'
        },
        '&:focus': {
          outline: 'none'
        }
      },
      title: {
        height: fontSize,
        marginBottom: '8px'
      },
      titleText: {
        fontWeight: 600,
        fontSize: fontSize,
        lineHeight: fontSize
      },
      menuSectionContainer: {
        padding: `0 ${theme.spacing(4)}px 0 ${theme.spacing(2)}px`
      },
      border: {
        borderRight: `solid 1px ${separatorColor}`
      },
      item: {},

      color: {
        color: textColor
      }
    };
  });

const SectionTitle: React.FC<SectionTitleProps> = ({
  className,
  children,
  textClassName,
  typographyVariant = 'body1',
  renderEmptyTitleField
}) => {
  if ((!children && renderEmptyTitleField) || children) {
    return (
      <ListItem className={className}>
        <Typography className={textClassName} variant={typographyVariant}>
          {children}
        </Typography>
      </ListItem>
    );
  }
  return null;
};

const findTitle = (sections: MenuSectionProps[]): boolean => {
  const item = sections.find((section: MenuSectionProps): boolean => {
    if (section.title) return true;
    return false;
  });
  return item ? true : false;
};
const MultiMenu: React.FC<MultiMenuProps> = ({
  optionSections,
  id,
  keepMounted = true,
  textColor = '#2E2E2E',
  separatorColor = '#F1F1F1',
  anchorEl,
  selected,
  menuClassName,
  closeOnClick = true,
  handleKey,
  menuVariant = 'menu',
  defaultOptionClickHandler,
  handleClose
}) => {
  const MULTI_MENU = optionSections.length > 1;
  const TITLE_FOUND = findTitle(optionSections);
  const classes = useStyles(textColor, separatorColor)();
  const handleClick = (
    event: React.MouseEvent<HTMLLIElement>,
    onClick?: React.MouseEventHandler<HTMLLIElement>
  ): void => {
    if (onClick) {
      onClick(event);
    } else if (defaultOptionClickHandler) {
      defaultOptionClickHandler(event);
    }
    if (closeOnClick) {
      handleClose();
    }
  };

  return (
    <Menu
      variant={menuVariant}
      className={menuClassName}
      onKeyDown={handleKey}
      id={id}
      anchorEl={anchorEl}
      keepMounted={keepMounted}
      autoFocus
      open={anchorEl !== null}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      onClose={handleClose}
    >
      <div className={clsx({ [classes.multiMenuContainer]: MULTI_MENU }, classes.menuContainer)}>
        {optionSections.map((section: MenuSectionProps, i: number) => {
          const { options, label, title, className } = section;
          return (
            <span
              key={`menuSection_${label}`}
              aria-label={label}
              data-testid={anchorEl !== null ? `Open: ${label}` : null}
              className={clsx(
                { [classes.menuSectionContainer]: MULTI_MENU },
                classes.color,
                className ? className : null,
                {
                  [classes.border]: i !== optionSections.length - 1
                }
              )}
            >
              <SectionTitle
                className={classes.title}
                textClassName={classes.titleText}
                renderEmptyTitleField={(MULTI_MENU && TITLE_FOUND) || false}
              >
                {title}
              </SectionTitle>

              {options.map((option: MenuOptionProps, optionIndex: number) => {
                const { label: optionLabel, onClick, children, id: optionId } = option;
                return (
                  <MenuItem
                    key={`${label}_${optionLabel}`}
                    className={classes.item}
                    id={optionId}
                    selected={
                      selected ? i === selected.section && optionIndex === selected.option : false
                    }
                    autoFocus={
                      selected ? i === selected.section && optionIndex === selected.option : false
                    }
                    onClick={(event: React.MouseEvent<HTMLLIElement>): void => {
                      handleClick(event, onClick);
                    }}
                  >
                    {children ? children : <Typography variant={'body1'}>{optionLabel}</Typography>}
                  </MenuItem>
                );
              })}
            </span>
          );
        })}
      </div>
    </Menu>
  );
};

const AppHeaderOptionsMenu: React.FC<AppHeaderOptionsMenuProps> = ({
  optionSections,
  id = 'app-header-options-menu',
  icon,
  iconClassName,
  buttonId,
  textColor,
  separatorColor,
  menuClassName,
  closeOnClick,
  buttonClassName,
  buttonSize,
  keepMounted = true,
  defaultOptionClickHandler,
  ...rest
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [selected, setSelected] = React.useState<{ section: number; option: number } | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const handleMoreOptionsMenuClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };
  const handleMoreOptionsMenuClose = (): void => {
    setSelected(null);
    setAnchorEl(null);
    if (buttonRef.current) {
      /*The purpose of this is to reset the focusRipple if something in dom hijacks focus.
      Without this focusRipple can get stuck when using non-click events to access the menu
      options */
      buttonRef.current.focus();
    }
  };

  const getOptionFromLeftRightKey = (comparedSection: number): number => {
    let option = 0;
    if (selected) {
      option = selected.option > comparedSection ? comparedSection : selected.option;
    }
    return option;
  };

  const handleKey: React.KeyboardEventHandler<HTMLDivElement> = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.key) {
      if (event.key === 'Escape') {
        handleMoreOptionsMenuClose();
      }
      if (selected) {
        switch (event.key) {
          case 'ArrowUp': {
            if (optionSections[selected.section].options.length > 1) {
              if (selected.option === 0) {
                setSelected({
                  ...selected,
                  option: optionSections[selected.section].options.length - 1
                });
              } else {
                setSelected({
                  ...selected,
                  option: selected.option - 1
                });
              }
            }
            break;
          }
          case 'ArrowDown':
            if (optionSections[selected.section].options.length > 1) {
              if (selected.option === optionSections[selected.section].options.length - 1) {
                setSelected({
                  ...selected,
                  option: 0
                });
              } else {
                setSelected({
                  ...selected,
                  option: selected.option + 1
                });
              }
            }
            break;
          case 'ArrowLeft':
            if (optionSections.length > 1) {
              if (selected.section === 0) {
                setSelected({
                  option: getOptionFromLeftRightKey(
                    optionSections[optionSections.length - 1].options.length - 1
                  ),
                  section: optionSections.length - 1
                });
              } else {
                setSelected({
                  option: getOptionFromLeftRightKey(
                    optionSections[selected.section - 1].options.length - 1
                  ),
                  section: selected.section - 1
                });
              }
            }

            break;
          case 'ArrowRight':
            if (optionSections.length > 1) {
              if (selected.section === optionSections.length - 1) {
                setSelected({
                  option: getOptionFromLeftRightKey(optionSections[0].options.length - 1),
                  section: 0
                });
              } else {
                setSelected({
                  option: getOptionFromLeftRightKey(
                    optionSections[selected.section + 1].options.length - 1
                  ),
                  section: selected.section + 1
                });
              }
            }
            break;
          default:
            break;
        }
      } else {
        switch (event.key) {
          case 'ArrowUp': {
            setSelected({
              option: optionSections[optionSections.length - 1].options.length - 1,
              section: optionSections.length - 1
            });
            break;
          }
          case 'ArrowDown': {
            setSelected({ option: 0, section: 0 });
            break;
          }
          case 'ArrowLeft': {
            setSelected({ option: 0, section: optionSections.length - 1 });
            break;
          }
          case 'ArrowRight': {
            setSelected({ option: 0, section: 0 });
            break;
          }
          default: {
            break;
          }
        }
      }
    }
  };

  return (
    <React.Fragment>
      <IconButton
        aria-controls={id}
        id={buttonId}
        size={buttonSize}
        ref={buttonRef}
        className={buttonClassName}
        aria-label={rest['aria-label']}
        aria-haspopup="true"
        onClick={handleMoreOptionsMenuClick}
      >
        {icon || <MoreVertIcon fontSize="large" className={iconClassName} />}
      </IconButton>
      <MultiMenu
        id={id}
        anchorEl={anchorEl}
        menuClassName={menuClassName}
        handleKey={handleKey}
        textColor={textColor}
        separatorColor={separatorColor}
        keepMounted={keepMounted}
        selected={selected}
        closeOnClick={closeOnClick}
        defaultOptionClickHandler={defaultOptionClickHandler}
        optionSections={optionSections}
        handleClose={handleMoreOptionsMenuClose}
      />
    </React.Fragment>
  );
};
export default AppHeaderOptionsMenu;
